import { pongBallPosition, PONG_POINTS_TO_WIN, type PingPongData } from '@veriverse/shared';
import { type Room } from 'colyseus.js';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isTyping } from '../game/typing.js';
import { sendStation, useServerNow, useStationData } from '../net/game-station.js';

const WIDTH = 320;
const HEIGHT = 160;
const PADDLE_X = [20, WIDTH - 20] as const;

/** Ping Pong panel (spec §6.14.6, timed-hit variant): press Space as the ball reaches your paddle. */
export function PongPanel({
  room,
  objectId,
  onClose,
}: {
  room: Room;
  objectId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const data = useStationData<PingPongData>(room, objectId);
  const { now, ready } = useServerNow(room, objectId, 'ping_pong_table');
  const canvas = useRef<HTMLCanvasElement>(null);
  const side = data?.players.findIndex((p) => p.id === room.sessionId) ?? -1;
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    if (data !== undefined && side < 0) onClose();
  }, [data, side, onClose]);

  const swing = () => {
    if (dataRef.current?.phase !== 'playing') return;
    sendStation(room, objectId, 'hit', { t: now() });
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || isTyping()) return;
      event.preventDefault();
      if (!event.repeat) swing();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, objectId]);

  useEffect(() => {
    let frame = 0;
    const draw = () => {
      const ctx = canvas.current?.getContext('2d');
      const current = dataRef.current;
      if (ctx) {
        ctx.fillStyle = '#1d4d3a';
        ctx.fillRect(0, 0, WIDTH, HEIGHT);
        ctx.fillStyle = '#eceff4';
        ctx.fillRect(WIDTH / 2 - 1, 0, 2, HEIGHT);
        ctx.fillRect(0, 0, WIDTH, 2);
        ctx.fillRect(0, HEIGHT - 2, WIDTH, 2);
        for (const x of PADDLE_X) {
          ctx.fillStyle = '#d4a017';
          ctx.fillRect(x - 3, HEIGHT / 2 - 18, 6, 36);
        }
        const ball = current?.phase === 'playing' ? current.ball : null;
        if (ball) {
          const { x, y } = pongBallPosition(ball, now());
          const px = PADDLE_X[0] + x * (PADDLE_X[1] - PADDLE_X[0]);
          const py = HEIGHT / 2 - y * 36;
          ctx.fillStyle = 'rgba(0,0,0,0.35)'; // shadow stays on the table, so the bounce reads
          ctx.fillRect(px - 3, HEIGHT / 2 + 4, 6, 3);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(px - 4, py - 4, 8, 8);
        }
      }
      frame = window.requestAnimationFrame(draw);
    };
    frame = window.requestAnimationFrame(draw);
    return () => window.cancelAnimationFrame(frame);
  }, [now]);

  const leave = () => {
    sendStation(room, objectId, 'leave');
    onClose();
  };
  const [left, right] = data?.players ?? [];
  const incoming =
    data?.phase === 'playing' && data.ball ? (data.ball.from === 0 ? 1 : 0) === side : false;
  const status = (() => {
    if (!data) return '';
    if (data.phase === 'over') {
      return data.winner === side ? t('games.pong.youWin') : t('games.pong.youLose');
    }
    if (data.phase === 'lobby') {
      return data.players.length < 2 ? t('games.pong.waiting') : t('games.pong.ready');
    }
    if (data.lastPoint && now() - data.lastPoint.at < 1_500) {
      const reason = t(`games.pong.reason.${data.lastPoint.reason}`);
      return data.lastPoint.winner === side
        ? t('games.pong.pointYou', { reason })
        : t('games.pong.pointThem', { reason });
    }
    return incoming ? t('games.pong.hitNow') : t('games.pong.watch');
  })();

  return (
    <div className="hud-box game-panel" role="dialog" aria-label={t('games.pong.title')}>
      <div className="game-panel-header">
        <strong>{t('games.pong.title')}</strong>
        <button type="button" className="secondary" onClick={leave}>
          {t('games.leave')}
        </button>
      </div>
      <div className="pong-score">
        <span>
          {left?.name ?? '…'} {left?.score ?? 0}
        </span>
        <span>
          {right?.score ?? 0} {right?.name ?? '…'}
        </span>
      </div>
      <canvas ref={canvas} width={WIDTH} height={HEIGHT} aria-label={t('games.pong.table')} />
      <p className="notice" role="status" aria-live="polite">
        {status}
      </p>
      <p className="notice">{t('games.pong.help', { points: PONG_POINTS_TO_WIN })}</p>
      <div className="game-panel-actions">
        {data?.phase !== 'playing' && (
          <button
            type="button"
            disabled={!ready || (data?.players.length ?? 0) < 2}
            onClick={() => sendStation(room, objectId, 'start')}
          >
            {data?.phase === 'over' ? t('games.pong.again') : t('games.pong.start')}
          </button>
        )}
        {data?.phase === 'playing' && (
          <button type="button" onClick={swing}>
            {t('games.pong.hit')}
          </button>
        )}
      </div>
    </div>
  );
}
